import apiClient from './api.service';

function unwrap<T>(response: { data?: any }): T {
  const d = response?.data?.data ?? response?.data;
  return d as T;
}

export type ProjectHealthLevel = 'Healthy' | 'Attention needed' | 'Financial risk' | 'Cancelled';
export type ProjectSignalLevel = 'green' | 'amber' | 'red';

export interface ProjectLinkedSalesOrderRow {
  id: string;
  sales_order_number: string;
  customer_name: string;
  order_status: string;
  required_date: string;
  promised_date: string;
  grand_total: number;
  is_fully_invoiced: boolean;
  is_partner: boolean;
  order_health_signal: { level: ProjectSignalLevel; summary: string };
  link_path: string;
}

export interface ProjectStatusCheckResponse {
  project: {
    id: string;
    project_number: string;
    project_name: string;
    customer_name: string;
    project_status: string;
    completion_percentage: number;
    start_date: string;
    planned_end_date: string;
    contract_value: number;
    total_budgeted_cost: number;
    sales_order_id?: string;
    quotation_id?: string;
    opportunity_id?: string;
  };
  partner_sales_order?: { id: string; sales_order_number: string; link_path: string };
  linked_sales_orders: ProjectLinkedSalesOrderRow[];
  commercial_rollup: {
    contract_value: number;
    ordered_total: number;
    invoiced_total: number;
    collected_total: number;
    ar_outstanding: number;
    due_overdue_outstanding: number;
    epc_contract_value?: number | null;
    epc_certified_to_date?: number | null;
    epc_retention_held?: number | null;
  };
  financial_position?: {
    cost_control_active: boolean;
    eac: number;
    reconciled_actual: number;
    overrun_flags: string[];
    drill: Record<string, string>;
    accrued_unbilled_cost?: {
      account_id: string | null;
      account_code: string | null;
      account_name: string | null;
      net_debit_balance: number;
      liability_balance: number;
      debit_total: number;
      credit_total: number;
    };
  };
  work_orders: Array<{
    id: string;
    work_order_number: string;
    work_order_status: string;
    order_date: string;
    planned_quantity: number;
    produced_quantity: number;
    link_path: string;
  }>;
  deliveries: Array<{
    id: string;
    delivery_note_number: string;
    delivery_date: string;
    delivery_status: string;
    sales_order_id: string;
    link_path: string;
  }>;
  invoices: Array<{
    id: string;
    invoice_number: string;
    invoice_date: string;
    due_date: string;
    invoice_type: string;
    invoice_status: string;
    grand_total: number;
    balance_due: number;
    attributed_billed?: number;
    attributed_collected?: number;
    attributed_ar?: number;
    is_shared?: boolean;
    link_path: string;
  }>;
  job_costing: {
    budget_total: number;
    posted_cost_total: number;
    variance: number;
    total_hours: number;
    total_labor_cost: number;
    pending_timesheet_approvals: number;
    by_category: Array<{ category: string; total: number; count: number }>;
  };
  procurement: {
    open_pr_count: number;
    open_po_count: number;
    po_count: number;
    prs: Array<{ id: string; pr_number: string; pr_status: string; required_date: string; estimated_total: number; link_path: string }>;
    pos: Array<{
      id: string;
      po_number: string;
      po_status: string;
      po_date: string;
      delivery_date: string;
      supplier_name: string;
      grand_total: number;
      currency: string;
      link_path: string;
    }>;
    cost_rollup: {
      po_ordered: number;
      grn_received: number;
      supplier_invoiced: number;
      supplier_paid: number;
    };
  };
  estimation: {
    boqs: Array<{ id: string; boq_number: string; boq_name: string; status: string; total_amount: number; link_path: string }>;
    trading_rental_estimations: Array<{
      id: string;
      estimation_number: string;
      estimation_type: string;
      status: string;
      total_cost: number;
      link_path: string;
    }>;
    estimated_budget_total: number;
    opportunity?: { id: string; opportunity_number: string; opportunity_name: string; status: string; link_path: string };
    quotation?: { id: string; quotation_number: string; quotation_name: string; status: string; link_path: string };
  };
  quality: {
    open_ncr_count: number;
    critical_ncr_count: number;
    open_capa_count: number;
    pending_inspection_count: number;
    ncrs: Array<{ id: string; ncr_number: string; severity: string; status: string; link_path: string }>;
    capas: Array<{ id: string; capa_number: string; status: string; target_completion_date: string; link_path: string }>;
  };
  sub_projects: Array<{ id: string; project_number: string; project_name: string; project_status: string; completion_percentage: number; link_path: string }>;
  related_mocs: Array<{ id: string; moc_number: string; change_title: string; status: string; link_path: string }>;
  profitability: {
    estimation_budget: number;
    posted_cost: number;
    committed_cost: number;
    forecast: { revenue: number; cost: number; profit: number; margin_pct: number };
    forecast_basis?: 'ordered' | 'estimate';
    billed: { revenue: number; cost: number; profit: number; margin_pct: number };
    cash: { revenue: number; cost: number; profit: number; margin_pct: number };
  };
  performance?: import('../types/estimationPerformance').EstimationPerformanceDto;
  blockers: string[];
  health: ProjectHealthLevel;
}

export async function getProjectStatusCheck(projectId: string): Promise<ProjectStatusCheckResponse> {
  const response = await apiClient.get(`/projects/${projectId}/status-check`);
  return unwrap<ProjectStatusCheckResponse>(response);
}
